import { axios } from '@/services/axios';
import { CheckCircleIcon, NotAllowedIcon } from '@chakra-ui/icons';
import {
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
    Skeleton,
    Tooltip,
} from '@chakra-ui/react';
import { accordionHeadingStyling } from '@/components/forms/new/formStyles';
import { classNames } from '@/utils/classNames';
import { useQuery } from 'hooks/useCustomQuery';
import { Alert, AlertIcon, Button, Heading, Text } from 'components/v4';
import { isEmpty } from 'lodash';
import { useState, useEffect } from 'react';
// (2993) Reverted — commented out rather than deleted, see commit cfe2c9c58.
// import { forwardRef, useImperativeHandle } from 'react';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { useServices } from './ServicesContext';
import ServicesAttributes from './attributes/ServicesAttributes';
import useServicesAttributesValidation from './attributes/useServicesAttributesValidation';

// (2993) Reverted — commented out rather than deleted, see commit cfe2c9c58.
// const Services = forwardRef(function Services(
//     {
//         companyID,
//         userID,
//         numberID,
//         trunkID,
//         definedAttributes: servicesCompany,
//         definedAttributesLoading: servicesCompanyLoading = false,
//         // When true, the per-service Save button is hidden and the caller is
//         // expected to persist changes itself via the exposed `submitAll` ref
//         // handle (e.g. folded into a page-level Save button).
//         hideIndividualSaveButton = false,
//     },
//     ref,
// ) {
export default function Services({
    companyID,
    userID,
    numberID,
    trunkID,
    definedAttributes: servicesCompany,
    definedAttributesLoading: servicesCompanyLoading = false,
}) {
    // Entity service = 0, company = 1, user = 2, number = 3, trunk =4
    const { dispatch, services, originalData, attributesErrors } =
        useServices();
    const { requiredScope } = useSelector((state) => {
        return { ...state.login };
    });
    const [isLoading, setIsLoading] = useState(false);
    const { darkMode } = useSelector((state) => state.settings);

    const { data: servicesData, isFetching: servicesLoading } = useQuery({
        // eg. user id
        queryKey: ['userID', userID],
        queryFn: async () => {
            const res = await axios.get(`user/${userID}/definedattributes`);
            return res.data;
        },
        enabled: Boolean(userID),
        onSuccess: (data) => {
            // Filter out deactivated services
            const filteredData =
                data?.filter((service) => service.activated !== false) || [];
            dispatch({
                type: 'ADD_SERVICES',
                payload: filteredData,
            });
        },
        onError: (err) => {
            toast.error(err?.response?.data?.message);
        },
        refetchOnWindowFocus: false,
        staleTime: 0,
    });

    const { data: servicesNumber, isFetching: servicesNumberLoading } =
        useQuery({
            // eg. user id
            queryKey: ['numberID', numberID],
            queryFn: async () => {
                const res = await axios.get(
                    `number/${numberID}/definedattributes`,
                );
                return res.data;
            },
            enabled: Boolean(numberID),
            onSuccess: (data) => {
                // Filter out deactivated services
                const filteredData =
                    data?.filter((service) => service.activated !== false) ||
                    [];
                dispatch({
                    type: 'ADD_SERVICES',
                    payload: filteredData,
                });
            },
            onError: (err) => {
                toast.error(err?.response?.data?.message);
            },
            refetchOnWindowFocus: false,
            staleTime: 0,
        });

    const { data: servicesTrunk, isFetching: servicesTrunkLoading } = useQuery({
        queryKey: ['trunkID', trunkID],
        queryFn: async () => {
            const res = await axios.get(`trunk/${trunkID}/definedattributes`, {
                headers: {
                    'X-RequestScope': requiredScope,
                },
            });
            return res.data;
        },
        enabled: Boolean(trunkID),
        onSuccess: (data) => {
            // Filter out deactivated services
            const filteredData =
                data?.filter((service) => service.activated !== false) || [];
            dispatch({
                type: 'ADD_SERVICES',
                payload: filteredData,
            });
        },
        onError: (err) => {
            toast.error(err?.response?.data?.message);
        },
        refetchOnWindowFocus: false,
        staleTime: 0,
    });

    const ufTitle = userID
        ? '/users'
        : numberID
          ? '/numbers'
          : trunkID
            ? '/trunks'
            : '/companies';

    // TODO: uncomment this when we have a proper API for detecting company services 3.39
    // const { data: servicesCompany, isFetching: servicesCompanyLoading } =
    //     useQuery({
    //         // eg. user id
    //         queryKey: ['companyID', companyID],
    //         queryFn: async () => {
    //             const res = await axios.get(
    //                 `company/${companyID}/definedattributes`,
    //             );
    //             return res.data;
    //         },
    //         enabled: Boolean(companyID),
    //         onSuccess: (data) => {
    //             dispatch({
    //                 type: 'ADD_SERVICES',
    //                 payload: data,
    //             });
    //         },
    //         onError: (err) => {
    //             toast.error(err?.response?.data?.message);
    //         },
    //         refetchOnWindowFocus: false,
    //         staleTime: 0,
    //     });

    const [index, setIndex] = useState(
        Array.from(
            {
                length:
                    servicesData?.length ||
                    servicesNumber?.length ||
                    servicesTrunk?.length ||
                    servicesCompany?.length,
            },
            (_, i) => i,
        ),
    );

    const {
        handleAttributeError,
        removeAttributeError,
        handleOGAttributeChange,
    } = useServicesAttributesValidation();

    const handleSubmit = async (servIndex) => {
        // get service and then attributes
        const serviceId = services[servIndex]?.service.id;
        const serviceAttributes = services[servIndex]?.attributes;
        let hasErrors = false;

        const errors = attributesErrors.some(
            (a) => a.serviceIndex === servIndex,
        );

        // if errors already exist, do not submit
        if (errors) {
            toast.error(
                'There are some validation errors. Please check and try again.',
            );
            return;
        }

        // check for errors and required fields
        for (const [index, att] of serviceAttributes.entries()) {
            const validation = JSON.parse(att.definedAttributeValidationType);

            if (validation.Required && isEmpty(att.value)) {
                handleAttributeError(index, 'Field is required.', servIndex);
                hasErrors = true;
            } else {
                removeAttributeError(index, servIndex);
            }
        }

        // has errors, do not proceed and show toast error
        if (hasErrors) {
            toast.error(
                'There are some validation errors. Please check and try again.',
            );
            return;
        }

        setIsLoading(true);

        // attributes to be updated
        for (const [index, att] of serviceAttributes.entries()) {
            try {
                // if attribute value are the same (no changes), skip the update
                if (
                    originalData[servIndex]?.attributes.some(
                        (b) => b.value === att.value,
                    )
                )
                    continue;
                const resourceID = userID || numberID || trunkID || companyID;
                const resourceType = userID
                    ? 'user'
                    : numberID
                      ? 'number'
                      : trunkID
                        ? 'trunk'
                        : 'company';

                // need to pass serviceId for number, user, and trunk defined attributes
                if (resourceType != 'company') {
                    await axios.put(
                        `/${resourceType}/${resourceID}/service/${serviceId}/definedattribute`,
                        {
                            ID: att.id,
                            value: att.value,
                        },
                        {
                            headers: {
                                'X-RequestScope': requiredScope,
                            },
                        },
                    );
                } else {
                    await axios.put(
                        `/${resourceType}/${resourceID}/definedattribute`,
                        {
                            ID: att.id,
                            value: att.value,
                        },
                    );
                }

                // update original data with new value as it is successful
                handleOGAttributeChange(att.value, index, servIndex);
            } catch (e) {
                // need to add specific error message from the backend to the attribute error
                hasErrors = true;
                handleAttributeError(
                    index,
                    e?.response?.data?.message,
                    servIndex,
                );
            }
        }

        // error from backend, show toast error
        if (hasErrors) {
            toast.error(
                'There are some validation errors. Please check and try again.',
            );
            setIsLoading(false);
            return;
        }

        toast.success(
            `Successfully updated attributes for ${services[servIndex].service.name}!`,
        );

        setIsLoading(false);
    };

    // (2993) Reverted — commented out rather than deleted, see commit cfe2c9c58.
    // // Persists every service that has at least one changed attribute. Used
    // // when hideIndividualSaveButton delegates saving to a page-level Save
    // // button instead of the per-service one.
    // const handleSubmitAll = async () => {
    //     let allSuccessful = true;
    //
    //     for (const [servIndex, serv] of originalData.entries()) {
    //         const isDirty = serv.attributes?.some(
    //             (att, attIndex) =>
    //                 att.value !==
    //                 services[servIndex]?.attributes[attIndex]?.value,
    //         );
    //
    //         if (isDirty) {
    //             const success = await handleSubmit(servIndex);
    //             if (!success) allSuccessful = false;
    //         }
    //     }
    //
    //     return allSuccessful;
    // };
    //
    // // No deps array: this must always expose the latest closure (originalData,
    // // services and handleSubmit all change on every render), and re-running on
    // // every render is a no-op cost here — it only reassigns ref.current.
    // useImperativeHandle(ref, () => ({ submitAll: handleSubmitAll }));

    useEffect(() => {
        if (servicesCompany) {
            // Filter out deactivated services
            const filteredData =
                servicesCompany?.filter(
                    (service) => service.activated !== false,
                ) || [];
            dispatch({
                type: 'ADD_SERVICES',
                payload: filteredData,
            });
        }
    }, [servicesCompany, dispatch]);

    return servicesLoading ||
        servicesNumberLoading ||
        servicesTrunkLoading ||
        servicesCompanyLoading ? (
        <Accordion>
            {[...Array(5)].map((_, index) => (
                <AccordionItem key={index}>
                    <AccordionButton>
                        <Skeleton width="100%" height="20px" />

                        <AccordionIcon />
                    </AccordionButton>
                </AccordionItem>
            ))}
        </Accordion>
    ) : (
        <>
            {originalData?.length > 0 ? (
                <Accordion
                    index={index}
                    onChange={(e) => setIndex(e)}
                    allowMultiple
                    reduceMotion
                    allowToggle>
                    {originalData.map((serv, servIndex) => (
                        <AccordionItem key={serv?.service.id}>
                            <AccordionButton
                                as="div"
                                minH="3rem"
                                data-walkthroughid={
                                    ufTitle +
                                    '/services' +
                                    `/${serv.service.name}`
                                }>
                                <Heading
                                    sx={accordionHeadingStyling}
                                    flex="1"
                                    textAlign="left"
                                    className={classNames(
                                        darkMode ? 'text-white' : 'text-black',
                                    )}>
                                    {serv.service.name}
                                </Heading>

                                {serv.activated ? (
                                    <Tooltip label="Activated">
                                        <CheckCircleIcon
                                            mr={2}
                                            color="green.500"
                                        />
                                    </Tooltip>
                                ) : (
                                    <Tooltip label="Disabled">
                                        <NotAllowedIcon
                                            mr={2}
                                            color="red.500"
                                        />
                                    </Tooltip>
                                )}
                                {/* (2993) !hideIndividualSaveButton && */}
                                {serv.attributes?.some(
                                    (att, index) =>
                                        att.value !=
                                        services[servIndex]?.attributes[index]
                                            ?.value,
                                ) && (
                                    <Button
                                        data-walkthroughid="/services/button/save"
                                        size="sm"
                                        mr={2}
                                        isLoading={isLoading}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleSubmit(servIndex);
                                        }}>
                                        Save
                                    </Button>
                                )}
                                <AccordionIcon />
                            </AccordionButton>

                            {serv.attributes?.length > 0 ? (
                                <AccordionPanel>
                                    <ServicesAttributes
                                        serviceIndex={servIndex}
                                        isBusy={isLoading}
                                    />
                                </AccordionPanel>
                            ) : (
                                <AccordionPanel>
                                    <Text>
                                        No additional information required for
                                        this service.
                                    </Text>
                                </AccordionPanel>
                            )}
                        </AccordionItem>
                    ))}
                </Accordion>
            ) : (
                <Alert status="info">
                    <AlertIcon />
                    No services activated.
                </Alert>
            )}
        </>
    );
}
// (2993) }); ... export default Services;
